import { SortingState } from "@tanstack/react-table";

import { IWarehouseLocation } from "../../models/Location";
import { FirstListedBucket } from "../../models/listingOptimisation/IListingInsights";

export type LocationSearchMode = "equals" | "beginsWith";

/** Sort options the queue offers. The server orders the page; the derived ones also sort within it. */
export type QueueSortKey = "firstListed" | "priceGap" | "stock" | "health" | "listingReviewed";

export interface IQueueFilters {
  search: string;
  categoryId: string | null;
  /** Kept for the filter pill; the query only sends the id. */
  categoryName: string | null;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationBeginsWith: string;
  firstListedBucket: FirstListedBucket | null;
  /** No MPN and no OE number. */
  missingPartNumbers: boolean;
  /** Hide listings reviewed within this many days; null shows everything. */
  hideReviewedWithinDays: number | null;
  /** Live price more than 10% above the intelligent pricing average. */
  pricedAboveIntelligence: boolean;
  /** Individual missing-field chips (MPN, OEMPN, Brand, ...), the listings query's missingFields. */
  missingFields: string[];
  sort: QueueSortKey;
}

export const DEFAULT_HIDE_REVIEWED_DAYS = 30;

export const defaultQueueFilters: IQueueFilters = {
  search: "",
  categoryId: null,
  categoryName: null,
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationBeginsWith: "",
  firstListedBucket: null,
  missingPartNumbers: false,
  hideReviewedWithinDays: DEFAULT_HIDE_REVIEWED_DAYS,
  pricedAboveIntelligence: false,
  missingFields: [],
  sort: "firstListed",
};

export const QUEUE_FILTERS_STORAGE_KEY = "wilma:tableFilters:ebay-optimisation";

export interface IQueueSortOption {
  key: QueueSortKey;
  label: string;
  /** The TanStack sorting state the option maps to - the column id carries the server sort key in its meta. */
  sorting: SortingState;
}

// The column ids live in QueueRow; each derived column names the server key it falls back to in
// meta.sortKey, so the page is ordered oldest first while the rows within it sort by the signal.
export const QUEUE_SORT_OPTIONS: IQueueSortOption[] = [
  { key: "firstListed", label: "First listed, oldest", sorting: [{ id: "firstListed", desc: false }] },
  { key: "priceGap", label: "Price gap, largest", sorting: [{ id: "priceGap", desc: true }] },
  { key: "stock", label: "Stock, most", sorting: [{ id: "stock", desc: true }] },
  { key: "health", label: "Listing health, lowest", sorting: [{ id: "health", desc: false }] },
  { key: "listingReviewed", label: "Last reviewed, oldest", sorting: [{ id: "listingReviewed", desc: false }] },
];

export function sortingForQueueSort(sort: QueueSortKey): SortingState {
  return QUEUE_SORT_OPTIONS.find((option) => option.key === sort)?.sorting ?? QUEUE_SORT_OPTIONS[0].sorting;
}

/**
 * The `searchBody` for POST /part/listings (Listed mode) from the queue's filters. Anything unset
 * is left undefined so the API applies no filter for it.
 */
export function buildQueueSearchBody(filters: IQueueFilters): Record<string, unknown> {
  const locationEquals = filters.locationSearchMode === "equals";

  return {
    categoryIds: filters.categoryId ? [filters.categoryId] : undefined,
    locationSearchMode: filters.locationSearchMode,
    warehouseLocationId: locationEquals ? filters.warehouseLocation?.id : undefined,
    warehouseLocationBeginsWith:
      !locationEquals && filters.warehouseLocationBeginsWith ? filters.warehouseLocationBeginsWith : undefined,
    firstListedBucket: filters.firstListedBucket ?? undefined,
    missingPartNumbers: filters.missingPartNumbers ? true : undefined,
    hideReviewedWithinDays:
      filters.hideReviewedWithinDays != null && filters.hideReviewedWithinDays > 0
        ? filters.hideReviewedWithinDays
        : undefined,
    pricedAboveIntelligence: filters.pricedAboveIntelligence ? true : undefined,
    missingFields: filters.missingFields.length > 0 ? filters.missingFields : undefined,
  };
}

/** How many filters are narrowing the queue beyond the defaults (the reviewed window is a default, not a filter). */
export function countActiveQueueFilters(filters: IQueueFilters): number {
  let count = 0;
  if (filters.categoryId) count += 1;
  if (filters.warehouseLocation?.id || filters.warehouseLocationBeginsWith) count += 1;
  if (filters.firstListedBucket) count += 1;
  if (filters.missingPartNumbers) count += 1;
  if (filters.pricedAboveIntelligence) count += 1;
  count += filters.missingFields.length;
  if (filters.search.trim()) count += 1;
  return count;
}
